// header.js - Shared Header Component

function log(...args) {
  const LEVELS = new Set(['info', 'success', 'silent-success', 'warning', 'error', 'pending']);
  let type = 'info';
  if (args.length > 1 && typeof args[args.length - 1] === 'string' && LEVELS.has(args[args.length - 1])) {
    type = args.pop();
  }
  const msg = args.map(v => typeof v === 'string' ? v : JSON.stringify(v)).join(' ');
  if (window.debugLog) window.debugLog(msg, type);
  else console.log(msg);
}

// Track page load time to suppress auto-connect logs
window.AV_PAGE_LOAD_TIME = Date.now();

// Track connection flow to suppress intermediate disconnect messages
let connectionInProgress = false;
let connectionTimeout = null;

// Shared wallet state (persisted in localStorage for cross-tab sync)
const WalletState = {
  get: () => {
    const stored = localStorage.getItem('aquavaults_wallet');
    return stored ? JSON.parse(stored) : { connected: false, address: null };
  },
  set: (state) => {
    localStorage.setItem('aquavaults_wallet', JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('walletStateChanged', { detail: state }));
  },
  clear: () => {
    localStorage.removeItem('aquavaults_wallet');
    window.dispatchEvent(new CustomEvent('walletStateChanged', { detail: { connected: false, address: null } }));
  }
};

// ============================================================
// RESPONSIVE HEADER LAYOUT (MOBILE FIX)
// Ensures the wallet address never overlaps the logo/title on small screens,
// and renders the address *under* the Disconnect button.
// ============================================================
function applyMobileHeaderLayout() {
  const header = document.querySelector('.app-header');
  const center = document.querySelector('.header-center');
  const right = document.querySelector('.header-right');
  const connectBtn = document.getElementById('headerConnectBtn');
  const walletInfo = document.getElementById('headerWalletInfo');
  const walletAddress = document.getElementById('headerWalletAddress');

  if (!header || !center || !right || !connectBtn) return;

  const isMobile = window.matchMedia && window.matchMedia('(max-width: 520px)').matches;

  if (isMobile) {
    // Stack header sections (center on top, wallet controls below)
    header.style.flexWrap = 'wrap';
    header.style.rowGap = '10px';

    center.style.flex = '1 1 100%';
    center.style.justifyContent = 'center';

    right.style.flex = '1 1 100%';
    right.style.display = 'flex';
    right.style.flexDirection = 'column';
    right.style.alignItems = 'flex-end';
    right.style.gap = '6px';

    // Button first, address under it
    connectBtn.style.order = '1';
    if (walletInfo) {
      walletInfo.style.order = '2';
      walletInfo.style.width = '100%';
      walletInfo.style.justifyContent = 'flex-end';
    }

    // Prevent long strings from spilling (just in case)
    if (walletAddress) {
      walletAddress.style.maxWidth = '100%';
      walletAddress.style.whiteSpace = 'nowrap';
      walletAddress.style.overflow = 'hidden';
      walletAddress.style.textOverflow = 'ellipsis';
      walletAddress.style.textAlign = 'right';
    }
  } else {
    // Revert to CSS defaults
    header.style.flexWrap = '';
    header.style.rowGap = '';

    center.style.flex = '';
    center.style.justifyContent = '';

    right.style.flex = '';
    right.style.display = '';
    right.style.flexDirection = '';
    right.style.alignItems = '';
    right.style.gap = '';

    connectBtn.style.order = '';
    if (walletInfo) {
      walletInfo.style.order = '';
      walletInfo.style.width = '';
      walletInfo.style.justifyContent = '';
    }
    if (walletAddress) {
      walletAddress.style.maxWidth = '';
      walletAddress.style.whiteSpace = '';
      walletAddress.style.overflow = '';
      walletAddress.style.textOverflow = '';
      walletAddress.style.textAlign = '';
    }
  }
}

// Update header display
function updateHeaderDisplay() {
  const connectBtn = document.getElementById('headerConnectBtn');
  const walletInfo = document.getElementById('headerWalletInfo');
  const walletAddress = document.getElementById('headerWalletAddress');
  const walletBalance = document.getElementById('headerWalletBalance');
  
  if (!connectBtn) return;
  
  const state = WalletState.get();
  
  if (state.connected && state.address) {
    connectBtn.textContent = 'Disconnect';
    connectBtn.classList.add('connected');
    
    if (walletInfo) {
      walletInfo.style.display = 'flex';
      if (walletAddress) {
        walletAddress.textContent = `${state.address.slice(0, 4)}...${state.address.slice(-4)}`;
      }
    }
    
    // Update balance if available
    updateHeaderBalance();
  } else {
    connectBtn.textContent = 'Connect Wallet';
    connectBtn.classList.remove('connected');
    
    if (walletInfo) {
      walletInfo.style.display = 'none';
    }
  }
  
  // Keep mobile layout stable when wallet state changes
  applyMobileHeaderLayout();
}

// Update SOL balance in header
async function updateHeaderBalance() {
  const walletBalance = document.getElementById('headerWalletBalance');
  if (!walletBalance) return;
  
  const state = WalletState.get();
  if (!state.connected) return;
  
  try {
    if (window.AquaVaultsSwapper && typeof window.AquaVaultsSwapper.getBalance === 'function') {
      const balance = await window.AquaVaultsSwapper.getBalance('SOL');
      walletBalance.textContent = `${balance.toFixed(4)} SOL`;
    }
  } catch (err) {
    log('Error updating header balance:', err);
  }
}

// The unified wallet widget now handles connection UI
// We just need to listen for state changes
window.addEventListener('av:wallet', function(event) {
  const { connected, publicKey } = event.detail || {};
  
  if (connected && publicKey) {
    // Wallet connected
    connectionInProgress = false;
    
    // Clear connection timeout
    if (connectionTimeout) {
      clearTimeout(connectionTimeout);
      connectionTimeout = null;
    }
    
    WalletState.set({ connected: true, address: publicKey });
    
    // Sync with swapper
    if (window.AquaVaultsSwapper) {
      window.AquaVaultsSwapper.setWalletConnected(publicKey);
    }
    
    // Update UI
    updateHeaderDisplay();
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('walletStateChanged', {
      detail: { connected: true, address: publicKey }
    }));
    
    // Only log manual connections (suppress auto-connect during first 2 seconds)
    const timeSinceLoad = Date.now() - window.AV_PAGE_LOAD_TIME;
    if (timeSinceLoad > 2000) {
      log('Wallet connected: ' + publicKey.slice(0, 4) + '...' + publicKey.slice(-4), 'silent-success');
    }
  } else {
    // Wallet disconnected
    const wasConnected = WalletState.get().connected;
    
    WalletState.clear();
    
    // Sync with swapper
    if (window.AquaVaultsSwapper) {
      window.AquaVaultsSwapper.setWalletDisconnected();
    }
    
    // Update UI
    updateHeaderDisplay();
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('walletStateChanged', {
      detail: { connected: false, address: null }
    }));
    
    // Only log manual disconnects (suppress during initialization and connection flow)
    const timeSinceLoad = Date.now() - window.AV_PAGE_LOAD_TIME;
    if (timeSinceLoad > 2000 && !connectionInProgress && wasConnected) {
      log('Wallet disconnected', 'warning');
    }
  }
});

// Auto-connect on page load if previously connected
async function autoConnectWallet() {
  // The unified wallet widget handles auto-connect via its autoConnect config
  // We just need to check if wallet is already connected on load
  const state = WalletState.get();
  
  if (state.connected && state.address) {    
    // Wait for widget to load and check state
    setTimeout(() => {
      if (window.AquaVaultsWallet?.connected && window.AquaVaultsWallet?.publicKeyString) {
      } else {
        // Clear stale state if widget didn't reconnect
        WalletState.clear();
        updateHeaderDisplay();
      }
    }, 1000);
  }
}

// Initialize header
function initHeader() {
  console.log('=== initHeader() called ===');
  
  updateHeaderDisplay();

  // Apply responsive header layout immediately and on resize (mobile fix)
  applyMobileHeaderLayout();
  
  // Add click handler for connect/disconnect button - triggers wallet widget
  const connectBtn = document.getElementById('headerConnectBtn');
  if (connectBtn) {
    connectBtn.addEventListener('click', async () => {
      // Wait for wallet widget to be ready
      const waitForWidget = () => {
        return new Promise((resolve) => {
          if (window.AquaVaultsWallet) {
            resolve();
          } else {
            setTimeout(() => waitForWidget().then(resolve), 50);
          }
        });
      };
      
      await waitForWidget();
      
      if (window.AquaVaultsWallet.connected) {
        // Disconnect through widget
        try {
          // Clear wallet adapter auto-connect keys so autoConnect: true
          // does not immediately reconnect on next page load
          localStorage.removeItem('walletName');
          localStorage.removeItem('uw-adapter');

          // Click the Jupiter wallet button to disconnect
      const jupiterBtn = document.querySelector('#av-wallet button');
      if (jupiterBtn) {
        jupiterBtn.click();
      } else {
        console.warn('Jupiter wallet button not found');
      }
        } catch (err) {
          log('Error disconnecting wallet: ' + err.message, 'error');
        }
      } else {
        // Connect through widget
        try {
          // Click the Jupiter wallet button to open selection modal
      const jupiterBtn = document.querySelector('#av-wallet button');
      if (jupiterBtn) {
        jupiterBtn.click();
      } else {
        console.warn('Jupiter wallet button not found');
      }
        } catch (err) {
          if (err.code === 4001) {
            log('Wallet connection cancelled', 'warning');
          } else {
            log('Error connecting wallet: ' + err.message, 'error');
          }
        }
      }
    });
  }
  let __headerResizeT;
  window.addEventListener('resize', () => {
    clearTimeout(__headerResizeT);
    __headerResizeT = setTimeout(applyMobileHeaderLayout, 80);
  });

  // Set active tab based on current page
  function setActiveTab() {
    const path = window.location.pathname;
    const tabs = document.querySelectorAll('.nav-tab');
    
    console.log('setActiveTab called - path:', path);
    console.log('Found tabs:', tabs.length);
    
    tabs.forEach(tab => {
      tab.classList.remove('active');
      const page = tab.getAttribute('data-page');
      
      console.log('Tab:', page, 'Path:', path);
      
      if ((path === '/' || path === '/index.html') && page === 'home') {
        console.log('MATCHED HOME - adding active class');
        tab.classList.add('active');
      } else if ((path === '/swap' || path === '/swap/' || path === '/autoswap' || path === '/autoswap/') && page === 'swap') {
        console.log('MATCHED SWAP - adding active class');
        tab.classList.add('active');
      } else if ((path === '/about' || path === '/about/') && page === 'about') {
        tab.classList.add('active');
      } else if ((path === '/community-affiliates' || path === '/community-affiliates/') && page === 'communities') {
        tab.classList.add('active');
      } else if ((path === '/limit-order' || path === '/limit-order/') && page === 'limit-order') {
        tab.classList.add('active');
      } else if ((path === '/graduated' || path === '/graduated/') && page === 'graduated') {
        tab.classList.add('active');
      }
    });
  }

  // Call setActiveTab now that it's defined
  setActiveTab();

  // Listen for wallet state changes
  window.addEventListener('walletStateChanged', updateHeaderDisplay);
  
  // Listen for storage changes from OTHER tabs (cross-tab sync)
  window.addEventListener('storage', (event) => {
    if (event.key === 'aquavaults_wallet') {
      log('Wallet state changed in another tab', 'info');
      
      // Get the new state from localStorage
      const newState = event.newValue ? JSON.parse(event.newValue) : { connected: false, address: null };
      
      // Update swapper state to match
      if (window.AquaVaultsSwapper) {
        if (newState.connected && newState.address) {
          window.AquaVaultsSwapper.setWalletConnected(newState.address);
        } else {
          if (window.AquaVaultsSwapper.setWalletDisconnected) {
            window.AquaVaultsSwapper.setWalletDisconnected();
          }
        }
      }
      
      // Update header display
      updateHeaderDisplay();
      
      // Dispatch local event so swap-card also updates
      window.dispatchEvent(new CustomEvent('walletStateChanged', { detail: newState }));
    }
  });
  
  // Auto-connect after a brief delay to ensure swapper is loaded
  setTimeout(autoConnectWallet, 100);
  
  // Unified wallet widget handles disconnect and account change events
  
  // Update balance periodically if connected
  setInterval(() => {
    const state = WalletState.get();
    if (state.connected) {
      updateHeaderBalance();
    }
  }, 10000); // Every 10 seconds
}

// Export for global access
window.AquaVaultsHeader = {
  init: initHeader,
  updateDisplay: updateHeaderDisplay,
  updateBalance: updateHeaderBalance,
  getWalletState: () => {
    // Always check the widget first for current state
    if (window.AquaVaultsWallet?.connected && window.AquaVaultsWallet?.publicKeyString) {
      return {
        connected: true,
        address: window.AquaVaultsWallet.publicKeyString
      };
    }
    return WalletState.get();
  }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeader);
} else {
  initHeader();
}